import type { StaffUser } from '../../../services/auth.service';
import type { OpportunityDetail } from '../models/opportunity.model';
import { coerceText } from './coerce-text';

export function isDirectSourceType(
  value: unknown,
  options?: { sourceTypeLabel?: string | null },
): boolean {
  const code = coerceText(value).trim();
  if (code && code.toUpperCase() === 'DIRECT') {
    return true;
  }
  if (code && /^direct$/i.test(code)) {
    return true;
  }
  const label = coerceText(options?.sourceTypeLabel).trim();
  return /^direct$/i.test(label);
}

/** Creator display name for DIRECT source description (detail API may omit createdByName). */
export function resolveOpportunityCreatorDisplayName(
  detail: Pick<
    OpportunityDetail,
    'createdByName' | 'createdById' | 'activities'
  >,
): string | undefined {
  const fromDetail = detail.createdByName?.trim();
  if (fromDetail) {
    return fromDetail;
  }
  const activities = detail.activities ?? [];
  const createdActivity = activities.find((activity) => {
    const type = coerceText(activity.type).toUpperCase();
    const title = coerceText(activity.title);
    return type.includes('CREATED') || /opportunity created/i.test(title);
  });
  const fromActivity = createdActivity?.createdByName?.trim();
  if (fromActivity) {
    return fromActivity;
  }
  const fromMeta = coerceText(createdActivity?.meta?.['createdByName']).trim();
  return fromMeta || undefined;
}

/** Apply DIRECT source description label from creator before pack edit seeds render. */
export function enrichDirectSourceOpportunityDetail(
  detail: OpportunityDetail,
): OpportunityDetail {
  if (
    !isDirectSourceType(detail.sourceTypeCode, {
      sourceTypeLabel: detail.sourceTypeLabel,
    })
  ) {
    return detail;
  }
  const creatorName = resolveOpportunityCreatorDisplayName(detail);
  if (!creatorName) {
    return detail;
  }
  const label = resolveDirectSourceDescriptionLabel(
    detail.sourceTypeCode,
    detail.sourceDescriptionCode,
    detail.sourceDescriptionLabel,
    null,
    {
      createdByName: creatorName,
      sourceTypeLabel: detail.sourceTypeLabel,
    },
  );
  return {
    ...detail,
    createdByName: detail.createdByName ?? creatorName,
    sourceDescriptionLabel: label?.trim() ?? detail.sourceDescriptionLabel,
  };
}

function firstNonEmptyTrimmed(
  ...candidates: Array<string | undefined | null>
): string | undefined {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return undefined;
}

/** Default source description for DIRECT — logged-in staff user id + display label. */
export function directSourceDescriptionForUser(
  user: StaffUser | null | undefined,
): { value: string; label: string } | null {
  const value = user?.id?.trim();
  if (!value) {
    return null;
  }
  const label =
    firstNonEmptyTrimmed(user?.username, user?.name, user?.email) ?? value;
  return { value, label };
}

export type DirectSourceDescriptionContext = {
  /** Opportunity creator display name — used when an RM views/edits a DIRECT lead. */
  createdByName?: string | null;
  sourceTypeLabel?: string | null;
};

/** Prefer API label; for DIRECT + staff user id, use creator or logged-in display name when label is missing. */
export function resolveDirectSourceDescriptionLabel(
  sourceTypeCode: unknown,
  sourceDescriptionCode: string | null | undefined,
  sourceDescriptionLabel: string | null | undefined,
  user: StaffUser | null | undefined,
  context?: DirectSourceDescriptionContext,
): string | null | undefined {
  const code = coerceText(sourceDescriptionCode).trim();
  const label = coerceText(sourceDescriptionLabel).trim();
  if (!code) {
    return sourceDescriptionLabel;
  }
  const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const labelIsOpaque = label && uuidLike.test(label);
  if (label && label !== code && !labelIsOpaque) {
    return label;
  }
  if (label && label === code && !labelIsOpaque) {
    return label;
  }
  if (
    !isDirectSourceType(sourceTypeCode, {
      sourceTypeLabel: context?.sourceTypeLabel,
    })
  ) {
    return sourceDescriptionLabel;
  }
  const creatorName = coerceText(context?.createdByName).trim();
  if (creatorName && creatorName !== code) {
    return creatorName;
  }
  const direct = directSourceDescriptionForUser(user);
  if (direct?.value === code) {
    return direct.label;
  }
  return sourceDescriptionLabel;
}
